import React, {useEffect, useRef, useState} from 'react';
import debounce from 'lodash/debounce';
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import {Button, Divider, Flex, Form, Input, message, Modal, Popconfirm, Popover, Radio, Select, Space, Steps, Tag} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getAllApplicationsPageable} from "../../../../../store/slices/ApplicationsListSlice";
import {changeCurrentPage} from "../../../../../store/slices/tableController/ApplicationsListTableController";
import {saveForm} from "../../../../../store/slices/FormSaveSlice";
import {ClearOutlined, CommentOutlined, DatabaseOutlined, ExclamationCircleFilled, FileAddOutlined, MailOutlined, PlusOutlined, ReloadOutlined, UserOutlined} from "@ant-design/icons";
import FloatActionMenu from "../../../../../components/UI/FloatActionMenu/FloatActionMenu";
import {rejectApplicationById} from "../../../../../store/slices/ApplicationSlice";
import {clrs} from "../../../../../constants/colors";
import classes from "../application.module.css";
import {CopyToClipboard} from "react-copy-to-clipboard/src";
import {LocalName} from "../../../../../utils/LocalName";
import {CLIENT_BASE_URL} from "../../../../../constants/api";
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import BlackerBlueButton from "../../../../../components/UI/Buttons/BlackerBlueButton";
import FormService from "../../../../../services/FormService";
import ApplicationService from "../../../../../services/ApplicationService";
import RedButton from "../../../../../components/UI/Buttons/RedButton";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import {signContract} from "../../../../../utils/signContract";
import MyText from "../../../../../components/UI/Text/MyText";
import dayjs from "dayjs";
import MyTable from "../../../../../components/Table/MyTable";
import ScrollDownButton from "../../../../components/ToBottom/ScrollDownButton";

const {Step} = Steps;
const { confirm } = Modal;
const ApplicationList = () => {
    const {applications, hasMore, isLoading} = useSelector(state => state.applicationsList);
    const application = useSelector(state => state.application);

    const formSave = useSelector(state => state.formSave);

    const {currentPage, pageSize} = useSelector(state => state.applicationsListTableController);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const hasSelected = selectedRowKeys.length > 0;

    const [loadingFormMultipleSave, setLoadingFormMultipleSave] = useState(false);
    const [isLoadingSignApplications, setIsLoadingSignApplications] = useState(false);

    const [openApplicationCommentModal, setOpenApplicationCommentModal] = useState(false);
    const [applicationCommentForm] = Form.useForm();
    const applicationComment = Form.useWatch('applicationComment', applicationCommentForm);
    const applicationCommentInput = useRef(null)

    const [openApplicationNotifyModal, setOpenApplicationNotifyModal] = useState(false);
    const [applicationNotifyForm] = Form.useForm();
    const emailSubject = Form.useWatch('emailSubject', applicationNotifyForm);
    const emailMessage = Form.useWatch('emailMessage', applicationNotifyForm);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [filterForm] = Form.useForm();
    const status = Form.useWatch('status', filterForm);
    const categoryId = Form.useWatch('categoryId', filterForm);
    const fullName = Form.useWatch('fullName', filterForm);

    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // п.4.1: активные / архив заявок
    const [archived, setArchived] = useState(false);

    // ТЗ updated п.6.1: смена программы в заявке
    const [openChangeProgramModal, setOpenChangeProgramModal] = useState(false);
    const [changeProgramApplication, setChangeProgramApplication] = useState(null);
    const [changeProgramCategoryId, setChangeProgramCategoryId] = useState(null);
    const [isChangingProgram, setIsChangingProgram] = useState(false);

    async function handleChangeProgram() {
        if (!changeProgramApplication || !changeProgramCategoryId) {
            return;
        }
        setIsChangingProgram(true);
        await ApplicationService.changeApplicationCategory(changeProgramApplication.id, changeProgramCategoryId)
            .then(() => {
                message.success(<IntlMessage id={'success'}/>, 3);
                setOpenChangeProgramModal(false);
                setChangeProgramApplication(null);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived}));
            })
            .catch((error) => {
                // 409 — договор уже сформирован, программу менять нельзя
                if (error?.response?.status === 409) {
                    message.error(<IntlMessage id={'applicationsPage.changeProgramTooLate'}/>, 7);
                } else {
                    message.error(<IntlMessage id={'error'}/>, 5);
                }
            })
            .finally(() => setIsChangingProgram(false));
    }

    async function handleToggleArchiveApplication(id, value) {
        await ApplicationService.setApplicationArchived(id, value)
            .then(() => {
                message.success(<IntlMessage id={'success'}/>, 3);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived}));
            })
            .catch(() => message.error(<IntlMessage id={'error'}/>, 3));
    }

    async function handleAutoArchiveApplications() {
        await ApplicationService.autoArchiveApplications()
            .then(() => {
                message.success(<IntlMessage id={'success'}/>, 3);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived}));
            })
            .catch(() => message.error(<IntlMessage id={'error'}/>, 3));
    }

    const [openViewEventHistory, setOpenViewEventHistory] = useState(false);
    const [viewEventHistory, setViewEventHistory] = useState(null);

    // Debounced функция для загрузки данных
    const debouncedFetchApplications = useRef(
        debounce((status, categoryId, name, arch) => {
            dispatch(getAllApplicationsPageable({
                page: currentPage,
                limit: pageSize,
                applicationStatus: status,
                categoryId: categoryId,
                fullName: name,
                archived: arch
            }));
        }, 500) // задержка 500мс
    ).current;

    useEffect(() => {
        debouncedFetchApplications(selectedStatus, selectedCategoryId, fullName, archived);
        return () => {
            debouncedFetchApplications.cancel();
        };
    }, [selectedCategoryId, selectedStatus, fullName, archived]);

    useEffect(() => {
        if (openApplicationCommentModal) {
            applicationCommentInput.current.focus();
        }
    }, [openApplicationCommentModal]);

    const categoriesList = useSelector(state => state.categoriesList);

    const fetchData = async (params) => {
        return dispatch(getAllApplicationsPageable({...params, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived}));
    }

    const formatDate = (dateString) => {
        return dayjs(dateString).format('DD.MM.YYYY HH:mm');
    };

    const saveCurrentPageSettings = (page, pageSize) => {
        dispatch(changeCurrentPage({page: page, pageSize: pageSize}));
    };

    async function handleSaveForm(applicationId) {
        await dispatch(saveForm({applicationId: applicationId}));
        await dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
    }

    async function handleSaveFormMultiple() {
        setLoadingFormMultipleSave(true);
        await FormService.saveFormMultiple(selectedRowKeys)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>,5);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setLoadingFormMultipleSave(false);
                setSelectedRowKeys([]);
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>,5);
                setLoadingFormMultipleSave(false);
            })
    }

    async function handleDeleteApplicationMultiple() {
        setLoadingFormMultipleSave(true);
        await ApplicationService.deleteApplicationsById(selectedRowKeys)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>,5);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setLoadingFormMultipleSave(false);
                setSelectedRowKeys([]);
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>,5);
                setLoadingFormMultipleSave(false);
            })
    }

    // п.4.1: массово архивировать/разархивировать выбранные заявки
    async function handleArchiveApplicationMultiple() {
        setLoadingFormMultipleSave(true);
        await ApplicationService.setApplicationsArchived(selectedRowKeys, !archived)
            .then(() => {
                message.success(<IntlMessage id={'success'}/>,5);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setLoadingFormMultipleSave(false);
                setSelectedRowKeys([]);
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>,5);
                setLoadingFormMultipleSave(false);
            })
    }

    async function handleCommentApplicationMultiple() {
        setLoadingFormMultipleSave(true);
        setOpenApplicationCommentModal(false);
        await ApplicationService.commentApplicationsById(selectedRowKeys, applicationComment)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>,5);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setLoadingFormMultipleSave(false);
                setSelectedRowKeys([]);
                applicationCommentForm.resetFields();
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>,5);
                setLoadingFormMultipleSave(false);
            })
    }

    async function handleNotifyApplicationMultiple() {
        setLoadingFormMultipleSave(true);
        setOpenApplicationNotifyModal(false);
        await ApplicationService.notifyApplicationsById(selectedRowKeys, emailSubject, emailMessage)
            .then((r) => {
                message.success(<IntlMessage id={'success'}/>,5);
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setLoadingFormMultipleSave(false);
                setSelectedRowKeys([]);
                applicationNotifyForm.resetFields();
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>,5);
                setLoadingFormMultipleSave(false);
            })
    }

    async function handleSignContract(signatureType) {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await signContract(signatureType, selectedRowKeys, null, null)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch((reason) => {
                message.destroy();
                if (reason instanceof Error) {
                    const errorMessage = reason.message;
                    if (errorMessage === "NCAOpenError") {
                        message.error(<IntlMessage id="NCAOpenError" />, 5);
                    } else {
                        message.error(<IntlMessage id={'error'} />, 5);
                    }
                } else {
                    message.error(<IntlMessage id={'error'} />, 5);
                }
            }).finally(() => {
                dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
                setSelectedRowKeys([]);
                applicationNotifyForm.resetFields();
            });
    }


    const showRejectApplicationModal = (id, application) => {
        confirm({
            title: <IntlMessage id={'doYouWantReject'}/>,
            icon: <ExclamationCircleFilled />,
            okText: <IntlMessage id={'yes'}/>,
            maskClosable: true,
            cancelText: <IntlMessage id={'no'}/>,
            content: <Flex style={{width: "100%"}} align={"flex-start"} justify={"center"} gap={5} vertical>
                <p><IntlMessage id={'name'}/> : {application.name}</p>
                <p><IntlMessage id={'email'}/> : {application.email}</p>
                <p><IntlMessage id={'telNumber'}/> : {application.telNumber}</p>
            </Flex>,
            async onOk() {
                await dispatch(rejectApplicationById({id: id}));
                await dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }));
            },
            onCancel() {},
        });
    };

    const columns = [
        {
            title: <IntlMessage id={'categoryCourse'}/>,
            render: (_, record) => (
                <p>{LocalName.getName(record.category)}</p>
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'date'}/>,
            render: (_, record) => (
                <p>{new Date(record.date).toLocaleDateString()}</p>
            ),
            width: '7%',
        },
        {
            title: <IntlMessage id={'name'}/>,
            render: (_, record) => (
                <p>{record.studentName}</p>
            ),
            // width: '100%',
        },
        {
            title: <IntlMessage id={'email'}/>,
            render: (_, record) => (
                <p>{record.email}</p>
            ),
            // width: '100%',
        },
        {
            title: <IntlMessage id={'telNumber'}/>,
            render: (_, record) => (
                <p>{record.telNumber}</p>
            ),
            width: '10%',
        },
        {
            // Витрина корпоративных курсов: заявка приходит либо от компании, либо от физлица.
            // У старых заявок поля пустые — колонка тогда просто ничего не выводит.
            title: <IntlMessage id={'applicationsPage.applicantColumn'}/>,
            render: (_, record) => (
                record.applicantType === 'COMPANY'
                    ? (
                        <Flex vertical gap={2}>
                            <Tag color={clrs.blackerBlue} style={{width: "fit-content"}}>
                                <IntlMessage id={'applicationsPage.applicantCompany'}/>
                            </Tag>
                            <p style={{margin: 0}}>{record.companyName}</p>
                            {record.position && <p style={{margin: 0, fontSize: 12}}>{record.position}</p>}
                            {record.employeesCount && (
                                <p style={{margin: 0, fontSize: 12}}>
                                    <IntlMessage id={'applicationsPage.employeesCount'}/>: {record.employeesCount}
                                </p>
                            )}
                        </Flex>
                    )
                    : (record.applicantType === 'INDIVIDUAL'
                        ? <Tag color={clrs.grey} style={{width: "fit-content"}}>
                            <IntlMessage id={'applicationsPage.applicantIndividual'}/>
                        </Tag>
                        : null)
            ),
            width: '10%',
        },
        {
            title: <IntlMessage id={'status'}/>,
            render: (_, record) => (
                <Flex style={{width: "100%"}} align={"center"} justify={"center"} gap={20}>
                    {record.status === 'NEW' &&
                        <Tag color={clrs.green}>{record.status}</Tag>
                    }
                    {record.status === 'IN_PROGRESS' &&
                        <Tag color={clrs.blue}>{record.status}</Tag>
                    }
                    {record.status === 'FILLED' &&
                        <Tag color={clrs.grey}>{record.status}</Tag>
                    }
                    {record.status === 'REJECTED' &&
                        <Tag color={clrs.whiterRed}>{record.status}</Tag>
                    }
                    {record.status === 'COMPLETED' &&
                        <Tag color={clrs.blackerBlue}>{record.status}</Tag>
                    }
                    {record.status === 'CLIENT_SIGNED' &&
                        <Tag color={clrs.blueForIcon}>{record.status}</Tag>
                    }
                    {record.status === 'ADMIN_APPROVED' &&
                        <Tag color={clrs.orange} style={{color: "black"}}>{record.status}</Tag>
                    }
                    {record.status === 'DIRECTOR_APPROVED' &&
                        <Tag color={clrs.darkerGreen}>{record.status}</Tag>
                    }
                    {record.status === 'CONTRACT' &&
                        <Tag color={clrs.whiterGreen}>{record.status}</Tag>
                    }
                </Flex>
            ),
            width: '8%',
        },
        {
            title: <Popover content={<IntlMessage id={"personalDocuments"}/>}><MyText style={{fontWeight: 600}}>B1</MyText></Popover>,
            render: (_, record) => (
                <>
                    {record.personalDocs > 0 ? <PlusOutlined /> : record.personalDocs > 1 && record.personalDocs}
                </>
            ),
            width: '4%',
        },
        {
            title:  <Popover content={<IntlMessage id={"preferentialDocuments"}/>}><MyText style={{fontWeight: 600}}>B2</MyText></Popover>,
            render: (_, record) => (
                <>
                    {record.otherDocs > 0 ? <PlusOutlined /> : record.otherDocs > 1 && record.otherDocs}
                </>
            ),
            width: '4%',
        },
        {
            title: <Popover content={<IntlMessage id={"studentSignature"}/>}><MyText style={{fontWeight: 600}}>SS</MyText></Popover>,
            render: (_, record) => (
                <>
                    {/* ApplicationsListResponse: Lombok-геттер isStudentSigned() Jackson отдаёт как "studentSigned" (так же parentSigned, adult) */}
                    {record.studentSigned && <Popover content={<IntlMessage id={"signed"}/>}><PlusOutlined /></Popover>}
                </>
            ),
            width: '4%',
        },
        {
            title: <Popover content={<IntlMessage id={"parentSignature"}/>}><MyText style={{fontWeight: 600}}>PS</MyText></Popover>,
            render: (_, record) => (
                <>
                    {!record?.adult ? (record.parentSigned ? <Popover content={<IntlMessage id={"signed"}/>}><PlusOutlined /></Popover> : "" ) : <Popover content={<IntlMessage id={"noNeedParentSign"}/>}><MyText medium>N/N</MyText></Popover>}
                </>
            ),
            width: '4%',
        },
        // {
        //     title: <Popover content={<IntlMessage id={"directorConsent"}/>}><MyText style={{fontWeight: 600}}>DC</MyText></Popover>,
        //     render: (_, record) => (
        //         <>
        //             {record.form?.isDirectorAgreed && <Popover content={<IntlMessage id={"DIRECTOR_AGREED"}/>}><PlusOutlined /></Popover>}                </>
        //     ),
        //     width: '4%',
        // },
        // {
        //     title: <Popover content={<IntlMessage id={"rectorSignature"}/>}><MyText style={{fontWeight: 600}}>RS</MyText></Popover>,
        //     render: (_, record) => (
        //         <>
        //             {!!record.form?.directorSignature && <Popover content={<IntlMessage id={"signed"}/>}><PlusOutlined /></Popover>}                </>
        //     ),
        //     width: '4%',
        // },
        {
            title: <IntlMessage id={'applicationsPage.comment'}/>,
            render: (_, record) => (
                <p>{record.comment}</p>
            ),
            width: '20%',
        },
        {
            title: <IntlMessage id={'form'}/>,
            render: (_, record) => (
                <>
                    {
                        <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                            <p onClick={() => {
                                setViewEventHistory(record?.events);
                                setOpenViewEventHistory(true);
                            }} className={classes.historyEventsBtn}><IntlMessage id={'eventHistory'}/></p>
                            <p onClick={() => {
                                setSelectedRowKeys([record.id]);
                                setOpenApplicationCommentModal(true);
                            }} className={classes.copyLinkBtn}><IntlMessage id={'applicationsPage.comment'}/></p>
                            {/* п.4.1: архивировать/разархивировать заявку */}
                            <p onClick={() => handleToggleArchiveApplication(record.id, !archived)}
                               className={classes.copyLinkBtn}>
                                <IntlMessage id={archived ? 'contracts.unarchive' : 'contracts.archive'}/>
                            </p>
                            {/* ТЗ updated п.6.1: сменить программу, пока договор не сформирован */}
                            {
                                ['NEW', 'IN_PROGRESS', 'FILLED'].includes(record.status) &&
                                <p onClick={() => {
                                    setChangeProgramApplication(record);
                                    setChangeProgramCategoryId(null);
                                    setOpenChangeProgramModal(true);
                                }} className={classes.copyLinkBtn}>
                                    <IntlMessage id={'applicationsPage.changeProgram'}/>
                                </p>
                            }
                            {/*{*/}
                            {/*    record.status !== 'CONTRACT' ?*/}
                                    <>
                                        {record.status === 'NEW' &&
                                            <p onClick={() => {
                                                handleSaveForm(record.id)
                                            }} className={classes.copyLinkBtn}><IntlMessage id={'createForm'}/></p>
                                        }
                                        {/*{(record.status !== 'NEW' && record.status !== 'REJECTED')*/}
                                        {/*    &&*/}
                                        {/*    <>*/}
                                        {/*        <CopyToClipboard text={CLIENT_BASE_URL + "/form/" + record.form.id} onCopy={() => {*/}
                                        {/*            message.success(<IntlMessage id={'linkCopied'}/>, 5);*/}
                                        {/*        }} ><p className={classes.copyLinkBtn}><IntlMessage id={'copyLinkShort'}/></p></CopyToClipboard>*/}
                                        {
                                            record.formId &&
                                            <a href={CLIENT_BASE_URL + "/form/" + record.formId} target={"_blank"}
                                               className={classes.copyLinkBtn}><IntlMessage id={'open'}/></a>
                                        }
                                        {/*    </>*/}
                                        {/*}*/}
                                        {record.status !== 'REJECTED' &&
                                            <p onClick={() => {
                                                showRejectApplicationModal(record.id, record)
                                            }} className={classes.rejectBtn}><IntlMessage id={'reject'}/></p>
                                        }
                                    </>
                                    {/*// :*/}
                                    {/*// <>*/}
                                    {/*//     <a href={CLIENT_BASE_URL + "/document/" + record.form.documentId}*/}
                                    {/*//        target={"_blank"}*/}
                                    {/*//        className={classes.copyLinkBtn}>Открыть</a>*/}
                                    {/*</>*/}
                            {/*// }*/}
                        </Flex>
                    }

                </>
            ),
            width: '15%',
        },
    ];

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
            <BlockLoading isLoading={formSave.isLoading || application.isLoading || loadingFormMultipleSave}/>
            <ScrollDownButton/>
            {/* п.5.1: плавающее меню действий (дубль верхних кнопок) */}
            <FloatActionMenu actions={[
                {key: 'clear', label: <IntlMessage id={'clear'}/>, icon: <ClearOutlined/>, onClick: () => {
                    setSelectedCategoryId(null);
                    setSelectedStatus(null);
                    filterForm.resetFields();
                }},
                {key: 'update', label: <IntlMessage id={'update'}/>, icon: <ReloadOutlined/>, onClick: () => {
                    dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived}));
                }},
                hasSelected && {key: 'createForm', label: <IntlMessage id={'createForm'}/>, icon: <FileAddOutlined/>, onClick: handleSaveFormMultiple},
                hasSelected && {key: 'comment', label: <IntlMessage id={'applicationsPage.comment'}/>, icon: <CommentOutlined/>, onClick: () => setOpenApplicationCommentModal(true)},
                hasSelected && {key: 'notify', label: <IntlMessage id={'applicationsPage.notification'}/>, icon: <MailOutlined/>, onClick: () => setOpenApplicationNotifyModal(true)},
            ]}/>
            <Flex className={classes.stickyToolbar} style={{width: "100%"}} align={"center"} justify={"center"} gap={20} wrap={"wrap"}>
                <GreyButton
                    onClick={() => {
                        setSelectedCategoryId(null);
                        setSelectedStatus(null);
                        filterForm.resetFields();
                    }}
                    backgroundColor={clrs.blackerBlue} fontColor={clrs.white}><IntlMessage id={'clear'}/></GreyButton>
                <GreyButton onClick={() => {
                    dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }))
                }} backgroundColor={clrs.whiter} fontColor={clrs.black}><IntlMessage id={'update'}/></GreyButton>
                {/* п.4.1: активные / архив заявок */}
                <Radio.Group value={archived} onChange={(e) => setArchived(e.target.value)} optionType="button" buttonStyle="solid">
                    <Radio.Button value={false}><IntlMessage id={'contracts.active'}/></Radio.Button>
                    {/* ТЗ updated: вкладка называется «Архив»; «В архив» осталось названием действия */}
                    <Radio.Button value={true}><IntlMessage id={'contracts.archiveTab'}/></Radio.Button>
                </Radio.Group>
                <Popconfirm
                    title={<IntlMessage id={'contracts.autoArchive'}/>}
                    onConfirm={handleAutoArchiveApplications}
                    okText={<IntlMessage id={'yes'}/>}
                    cancelText={<IntlMessage id={'no'}/>}
                >
                    <GreyButton backgroundColor={clrs.whiter} fontColor={clrs.black}>
                        <IntlMessage id={'contracts.autoArchive'}/>
                    </GreyButton>
                </Popconfirm>
                {
                    hasSelected &&
                    <BlackerBlueButton onClick={handleSaveFormMultiple}>
                        <IntlMessage id={'createForm'}/>
                    </BlackerBlueButton>
                }
                {
                    hasSelected &&
                    <BlackerBlueButton onClick={() => {setOpenApplicationCommentModal(true)}}>
                        <IntlMessage id={'applicationsPage.comment'}/>
                    </BlackerBlueButton>
                }
                {
                    hasSelected &&
                    <GreenButton onClick={() => {setOpenApplicationNotifyModal(true)}}>
                        <IntlMessage id={'applicationsPage.notification'}/>
                    </GreenButton>

                }
                {/* п.4.1: массово архивировать/разархивировать выбранные заявки */}
                {
                    hasSelected &&
                    <Popconfirm
                        title={<IntlMessage id={archived ? 'contracts.unarchive' : 'contracts.archive'}/>}
                        onConfirm={handleArchiveApplicationMultiple}
                        okText={<IntlMessage id={'yes'}/>}
                        cancelText={<IntlMessage id={'no'}/>}
                    >
                        <GreyButton backgroundColor={clrs.orange} fontColor={clrs.black}>
                            <IntlMessage id={archived ? 'contracts.unarchive' : 'contracts.archive'}/>
                        </GreyButton>
                    </Popconfirm>
                }
                {
                    hasSelected &&
                    <Popconfirm
                        title={<IntlMessage id={'delete'}/>}
                        description={<IntlMessage id={'formPage.youSureToDelete'}/>}
                        onConfirm={handleDeleteApplicationMultiple}
                        // onCancel={cancel}
                        okText={<IntlMessage id={'yes'}/>}
                        cancelText={<IntlMessage id={'no'}/>}
                    >
                        <RedButton>
                            <IntlMessage id={'delete'}/>
                        </RedButton>
                    </Popconfirm>
                }
                {/*{*/}
                {/*    hasSelected &&*/}
                {/*    <GreenButton onClick={() => handleSignContract("director", selectedRowKeys, null, null)}>*/}
                {/*        <IntlMessage id={'sign'}/>*/}
                {/*    </GreenButton>*/}

                {/*}*/}

            </Flex>
            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
                <Form onKeyUp={(e) => {if (e.key === "Enter") {
                    dispatch(getAllApplicationsPageable({page: currentPage, limit: pageSize, applicationStatus: selectedStatus, selectedCategoryId, fullName, archived }))
                }}} form={filterForm} className={classes.form}>
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"}>
                        <Divider className={classes.divider} orientation="left">
                            <IntlMessage id={'fullName'}/>/<IntlMessage id={'telNumber'}/>
                        </Divider>
                        <Form.Item name="fullName" className={classes.formInputBlock}>
                            <Input className={classes.formInput} placeholder={"Азамат/8707"}/>
                        </Form.Item>
                    </Flex>
                    <Flex style={{width: "100%", position: "relative"}} align={"flex-start"} justify={"flex-start"}>
                        <Flex style={{width: "50%"}} vertical align={"flex-start"} justify={"flex-start"}>
                            <Divider className={classes.divider} orientation="left"><IntlMessage id={'category'}/></Divider>
                            <Form.Item name="categoryId" >
                                <Radio.Group value={selectedCategoryId}
                                             defaultValue={null}
                                             onChange={(e) => {
                                                 setSelectedCategoryId(e.target.value)
                                             }}>>
                                    <Space wrap={"wrap"}>
                                        <Radio value={null} key="all">
                                            <IntlMessage id={"allCategories"}/>
                                        </Radio>
                                        {
                                            categoriesList.categories.map((category, i) => {
                                                return (
                                                    <Radio value={category.id} key={category.id}>{LocalName.getName(category)}</Radio>
                                                )
                                            })
                                        }
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </Flex>
                        <Flex style={{width: "50%"}} vertical align={"flex-start"} justify={"flex-start"}>
                            <Divider className={classes.divider} orientation="left"><IntlMessage id={'status'}/></Divider>
                            <Form.Item name="status" >
                                <Radio.Group  value={selectedStatus}
                                              defaultValue={null}
                                              onChange={(e) => {
                                                  setSelectedStatus(e.target.value)
                                              }}>>
                                    <Space wrap={"wrap"}>
                                        <Radio value={null} ><Tag color={clrs.black}><IntlMessage id={"ALL"}/></Tag></Radio>
                                        <Radio value={"NEW"} ><Tag color={clrs.green}>NEW</Tag></Radio>
                                        <Radio value={"IN_PROGRESS"} ><Tag color={clrs.blue}>IN_PROGRESS</Tag></Radio>
                                        <Radio value={"FILLED"} ><Tag color={clrs.grey}>FILLED</Tag></Radio>
                                        <Radio value={"REJECTED"} ><Tag color={clrs.whiterRed}>REJECTED</Tag></Radio>
                                        <Radio value={"COMPLETED"} ><Tag color={clrs.blackerBlue}>COMPLETED</Tag></Radio>
                                        <Radio value={"CLIENT_SIGNED"} ><Tag color={clrs.blueForIcon}>CLIENT SIGNED</Tag></Radio>
                                        <Radio value={"ADMIN_APPROVED"} ><Tag color={clrs.orange} style={{color: "black"}}>ADMIN APPROVED</Tag></Radio>
                                        <Radio value={"DIRECTOR_APPROVED"} ><Tag color={clrs.darkerGreen}>DIRECTOR APPROVED</Tag></Radio>
                                        <Radio value={"CONTRACT"} ><Tag color={clrs.whiterGreen}>CONTRACT</Tag></Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </Flex>
                    </Flex>
                </Form>
            </Flex>
            <MyTable
                isLoading={isLoading}
                dataSource={applications}
                columns={columns}
                fetchData={fetchData}
                saveCurrentPage={saveCurrentPageSettings}
                initialPage={currentPage}
                initialPageSize={pageSize}
                hasMore={hasMore}
                expandable={true}
                expandedRowRender={record =>
                    <span>{record?.courses.map(item => item.nameRu).join(', ')}</span>
                }
                rowExpandable={record => (record?.category?.nameEng === 'Foundation' && record?.status !== "IN_PROGRESS" && record?.status !== "NEW")}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
            />

            {/* ТЗ updated п.6.1: смена программы в заявке */}
            <Modal
                width={500}
                maskClosable={true}
                title={<IntlMessage id={'applicationsPage.changeProgram'}/>}
                open={openChangeProgramModal}
                onOk={handleChangeProgram}
                confirmLoading={isChangingProgram}
                okButtonProps={{disabled: !changeProgramCategoryId}}
                onCancel={() => setOpenChangeProgramModal(false)}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
            >
                <Flex vertical gap={12} style={{width: "100%"}}>
                    <p><IntlMessage id={'applicationsPage.changeProgramHint'}/></p>
                    <Select
                        style={{width: "100%"}}
                        value={changeProgramCategoryId}
                        onChange={(value) => setChangeProgramCategoryId(value)}
                        placeholder={IntlMessageText.getText({id: 'category'})}
                        options={
                            categoriesList.categories
                                .filter(category => !!category.formType)
                                .map(category => ({value: category.id, label: LocalName.getName(category)}))
                        }
                    />
                </Flex>
            </Modal>
            <Modal
                width={500}
                maskClosable={true}
                title={<IntlMessage id={'applicationsPage.comment'}/>}
                open={openApplicationCommentModal}
                onOk={handleCommentApplicationMultiple}
                onCancel={() => {setOpenApplicationCommentModal(false)}}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
            >
                <Form form={applicationCommentForm} className={classes.form}>
                    <Form.Item name="applicationComment" className={classes.formInputBlock}>
                        <Input.TextArea ref={applicationCommentInput} className={classes.formInput} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                width={800}
                maskClosable={true}
                title={<IntlMessage id={'applicationsPage.notification'}/>}
                open={openApplicationNotifyModal}
                onOk={handleNotifyApplicationMultiple}
                onCancel={() => {setOpenApplicationNotifyModal(false)}}
                okText={<IntlMessage id={'send'}/>}
                cancelText={<IntlMessage id={'close'}/>}
            >
                <Form
                    form={applicationNotifyForm}
                    className={classes.form}
                    initialValues={{
                        emailSubject: "Курсы английского \"General English\"",
                        emailMessage: "Добрый день! Продолжите пожалуйста регистрацию на курс английского языка \"General English\". Пройдите по ссылке ({form_link})"
                    }}
                >
                    <Form.Item
                        label={<IntlMessage id={'applicationsPage.notification.subject'}/>}
                        labelCol={{ span: 24 }}
                        name="emailSubject" className={classes.formInputBlock}>
                        <Input className={classes.formInput} />
                    </Form.Item>
                    <Form.Item
                        label={<IntlMessage id={'applicationsPage.notification.message'}/>}
                        labelCol={{ span: 24 }}
                        name="emailMessage" className={classes.formInputBlock}>
                        <Input.TextArea
                            style={{ height: 120}}
                            className={classes.formInput} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewEventHistory}
                // okText={"OK"}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewEventHistory(false)
                }}
                onCancel={() => {
                    setOpenViewEventHistory(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewEventHistory(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewEventHistory &&
                    <Steps progressDot direction="vertical"  current={viewEventHistory.length}>
                        {viewEventHistory.map((item, index) => (
                            <Step
                                key={index}
                                title={
                                    <Flex gap={"small"}>
                                        {item?.type === "SYSTEM" ? <DatabaseOutlined/> : <UserOutlined/>}
                                        <IntlMessage id={item?.activity}/> {!!item?.fullName && " (" + item?.fullName + ")"}
                                    </Flex>}
                                description={formatDate(item?.createdAt)}
                            />
                        ))
                        }
                    </Steps>
                }
            </Modal>
        </Flex>
    );
};

export default ApplicationList;